import React from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    Collapse,
    FormControlLabel,
    Grid,
    IconButton,
    Typography
} from "@mui/material";
import FeatherIcon from "feather-icons-react";

import Breadcrumb from "../../../layouts-v2/full-layout/breadcrumb/Breadcrumb";

import PageContainer from "../../../components-v2/container/PageContainer";

import CustomFormLabel from "../../../components-v2/forms/custom-elements/CustomFormLabel";
import CustomCheckbox from "../../../components-v2/forms/custom-elements/CustomCheckbox";
import CustomTextField from "../../../components-v2/forms/custom-elements/CustomTextField";
import { EitriAppService } from "../../../services-v2/eitri-manager-api/EitriAppService";
import { ApplicationService } from "../../../services-v2/eitri-manager-api/ApplicationService";
import { useParams } from "react-router-dom";
import { EitriAppStatus } from "../../../models-v2/EitriApp";
import useForm from "../../../hooks/useForm";
import useStatus from "../../../hooks/useStatus";
import useAlert from "../../../hooks/useAlert";
import Loading from "../../../layouts-v2/loading/Loading";

const EitriAppEdit = () => {
    const [eitriAppName, setEitriAppName] = React.useState("");
    const [application, setApplication] = React.useState({ name: "" });
    const { values, setValue, setAllValues } = useForm({
        id: "",
        name: "",
        slug: "",
        publicKey: "",
        description: "",
        status: "",
        visible: false
    });
    const { status, setAllStatus } = useStatus();
    const { alertInfo, setAllAlertInfo, setInitialAlertInfo } = useAlert();
    const params = useParams();

    React.useEffect(() => {
        initializeView();
    }, []);

    async function initializeView() {
        setAllStatus({ isFetching: true, success: false, error: false });
        try {
            const eitriApp = await getEitriAppById(params.eitriAppId);
            await getApplicationById(eitriApp.applicationId);
            setAllStatus({ isFetching: false, success: true, error: false });
        } catch (err) {
            console.error(err);
            setAllStatus({ isFetching: false, success: false, error: true });
        }
    }

    async function getEitriAppById(eitriAppId) {
        try {
            const eitriApp = await EitriAppService.getEitriAppById(eitriAppId);
            setEitriAppName(eitriApp.name);
            setAllValues(eitriApp);
            return eitriApp;
        } catch (err) {
            console.error(err);
        }
    }

    async function getApplicationById(applicationId) {
        try {
            const application = await ApplicationService.getApplicationById(
                applicationId
            );
            setApplication(application);
        } catch (err) {
            console.error(err);
        }
    }

    async function updateEitriapp() {
        setAllStatus({ isFetching: true, success: false, error: false });
        try {
            await EitriAppService.updateEitriAppById(values);
            initializeView();
            setAllStatus({ isFetching: false, success: true, error: false });
            setAllAlertInfo({
                theme: "success",
                message: "Eitri App atualizado com sucesso!",
                visible: true
            });
        } catch (err) {
            console.error(err);
            setAllStatus({ isFetching: false, success: false, error: true });
            setAllAlertInfo({
                theme: "error",
                message: "Ops! Não foi possível atualizar o Eitri App.",
                visible: true
            });
        }
    }

    const changeEitriAppStatus = event => {
        let eitriAppDeepClone = JSON.parse(JSON.stringify(values));
        const updatedStatus =
            event.target.value === EitriAppStatus.ACTIVE
                ? EitriAppStatus.INACTIVE
                : EitriAppStatus.ACTIVE;
        eitriAppDeepClone.status = updatedStatus;
        setAllValues(eitriAppDeepClone);
    };

    const changeEitriAppVisibility = event => {
        let eitriAppDeepClone = JSON.parse(JSON.stringify(values));
        const updatedVisibility = event.target.value === "true" ? false : true;
        eitriAppDeepClone.visible = updatedVisibility;
        setAllValues(eitriAppDeepClone);
    };

    const BCrumb = [
        {
            to: "/eitri-app-management/eitri-apps",
            title: "Eitri Apps"
        },
        {
            title: "Editar Eitri App"
        }
    ];
    return (
        <>
            {status.isFetching ? (
                <Loading isVisible={status.isFetching} />
            ) : (
                <PageContainer
                    title="Eidtar Eitri App"
                    description="this is Customer Edit page"
                >
                    <Collapse in={alertInfo.visible}>
                        <Alert
                            variant="filled"
                            severity={alertInfo.theme || "info"}
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        setInitialAlertInfo();
                                    }}
                                >
                                    <FeatherIcon icon="x" width="20" />
                                </IconButton>
                            }
                        >
                            {alertInfo.message}
                        </Alert>
                    </Collapse>
                    <Breadcrumb items={BCrumb} />
                    <Grid container spacing={0}>
                        <Grid item lg={4} md={12} xs={12}>
                            <Card sx={{ p: 3 }}>
                                <Typography variant="h2" sx={{ mt: 1 }}>
                                    {eitriAppName}
                                </Typography>
                                <Typography variant="body2">
                                    {application.name.toLowerCase()}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    fontWeight="600"
                                    sx={{ mt: 3, mb: 1 }}
                                >
                                    Slug
                                </Typography>
                                <Typography variant="body2">
                                    {values.slug}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    fontWeight="600"
                                    sx={{ mt: 3, mb: 1 }}
                                >
                                    Chave Pública
                                </Typography>
                                <Typography variant="body2">
                                    {values.publicKey}
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item lg={8} md={12} xs={12}>
                            <Card sx={{ p: 3 }}>
                                <Typography
                                    variant="h6"
                                    fontWeight="600"
                                    sx={{ mb: 3 }}
                                >
                                    Atualizar Detalhes
                                </Typography>
                                <form>
                                    <CustomFormLabel htmlFor="name">
                                        Nome
                                    </CustomFormLabel>
                                    <CustomTextField
                                        id="name"
                                        name="name"
                                        variant="outlined"
                                        value={values.name}
                                        fullWidth
                                        size="small"
                                        sx={{ mb: 2 }}
                                        onChange={e => setValue(e)}
                                    />

                                    <CustomFormLabel htmlFor="Title">
                                        Título
                                    </CustomFormLabel>
                                    <CustomTextField
                                        id="Title"
                                        name="title"
                                        variant="outlined"
                                        value={values.title}
                                        fullWidth
                                        size="small"
                                        sx={{ mb: 2 }}
                                        onChange={e => setValue(e)}
                                    />

                                    <CustomFormLabel htmlFor="description">
                                        Descrição do Projeto
                                    </CustomFormLabel>
                                    <CustomTextField
                                        id="description"
                                        name="description"
                                        variant="outlined"
                                        multiline
                                        rows={4}
                                        value={
                                            values.description
                                                ? values.description
                                                : ""
                                        }
                                        fullWidth
                                        size="small"
                                        sx={{ mb: 2 }}
                                        onChange={e => setValue(e)}
                                    />
                                    <Box>
                                        <FormControlLabel
                                            control={
                                                <CustomCheckbox
                                                    name={"status"}
                                                    value={values.status}
                                                    checked={
                                                        values.status ===
                                                        EitriAppStatus.ACTIVE
                                                    }
                                                    onChange={e =>
                                                        changeEitriAppStatus(e)
                                                    }
                                                />
                                            }
                                            label="Ativo"
                                        />

                                        <FormControlLabel
                                            control={
                                                <CustomCheckbox
                                                    name={"visible"}
                                                    value={values.visible}
                                                    checked={values.visible}
                                                    onChange={e =>
                                                        changeEitriAppVisibility(
                                                            e
                                                        )
                                                    }
                                                />
                                            }
                                            label="Visível"
                                        />
                                    </Box>

                                    <Box
                                        display="flex"
                                        justifyContent="flex-end"
                                    >
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            onClick={() => updateEitriapp()}
                                        >
                                            Atualizar
                                        </Button>
                                    </Box>
                                </form>
                            </Card>
                        </Grid>
                    </Grid>
                </PageContainer>
            )}
        </>
    );
};

export default EitriAppEdit;
